import type { ResourceCredentialsUI } from "../types";

/**
 * Masking helpers for credential-bearing connection strings.
 *
 * Connection strings are masked by default in all ordinary rendering
 * (visible text, accessibility snapshots, DOM). The real value is
 * produced ONLY for an explicit, authorized copy action and is never
 * placed in a rendered text node, aria attribute, log, or analytics
 * event.
 */

/** Mask placeholder shown in place of a connection secret. */
export const MASKED_CONNECTION_STRING = "••••••••••••••••";

/**
 * Parse host and port from a resource endpoint string.
 * Endpoint format: `host:port/database`.
 * Returns `{ host, port }` or null when the endpoint is missing/unparseable.
 */
export function parseEndpoint(
  endpoint: string | null | undefined,
): { host: string; port: string } | null {
  if (!endpoint) return null;
  const hostPort = endpoint.split("/")[0];
  const lastColon = hostPort.lastIndexOf(":");
  if (lastColon <= 0) return null;
  const host = hostPort.slice(0, lastColon);
  const port = hostPort.slice(lastColon + 1);
  if (!host || !port) return null;
  return { host, port };
}

/**
 * Build the real (credential-bearing) PostgreSQL connection string.
 *
 * SECURITY: The returned string contains the username and password.
 * It must NEVER be rendered directly in the DOM, placed in an aria
 * attribute, logged, or sent to analytics. Use {@link maskedConnString}
 * for display and expose this value ONLY through an explicit authorized
 * copy action.
 */
export function realConnString(
  creds: ResourceCredentialsUI,
  endpoint: string,
): string {
  const parts = parseEndpoint(endpoint);
  const hostPort = parts ? `${parts.host}:${parts.port}` : endpoint;
  return `postgresql://${creds.username}:${creds.password}@${hostPort}/${creds.database}`;
}

/**
 * Build a masked, safe-to-render PostgreSQL connection string.
 *
 * The credentials are replaced with a fixed mask so the rendered text
 * and accessibility snapshot never disclose the username or password.
 * The host, port, and database name are non-secret and remain visible
 * to aid connection debugging.
 */
export function maskedConnString(
  creds: ResourceCredentialsUI,
  endpoint: string,
): string {
  const parts = parseEndpoint(endpoint);
  const hostPort = parts ? `${parts.host}:${parts.port}` : endpoint;
  return `postgresql://••••••••:••••••••@${hostPort}/${creds.database}`;
}

/**
 * Build the real (credential-bearing) PostgreSQL connection string from
 * an endpoint alone, using the derived app-role username and no password.
 *
 * This is used only when real Vault credentials are unavailable so the
 * copy action still yields a usable template. The password placeholder
 * `REPLACE_WITH_VAULT_PASSWORD` makes it clear the value must be
 * substituted from Vault and is never a real secret.
 */
export function templateConnString(
  endpoint: string,
  database: string,
  username: string,
): string {
  const parts = parseEndpoint(endpoint);
  const hostPort = parts ? `${parts.host}:${parts.port}` : endpoint;
  return `postgresql://${username}:REPLACE_WITH_VAULT_PASSWORD@${hostPort}/${database}`;
}
